// Shared date/time display helpers.
//
// The DB stores audit/event timestamps as UTC DATETIME2 (SYSUTCDATETIME()) and the
// API serializes them WITHOUT an offset ("2026-10-22T20:51:12.123"). `new Date(s)`
// would read such a string as the browser's LOCAL time, so the UI would show UTC.
// Every timestamp must go through parseUtcTimestamp / formatDateTimeIL, and every
// DATE-only value ("yyyy-MM-dd") through formatDateOnly (pure string work, no
// timezone conversion - new Date('yyyy-MM-dd') is UTC midnight and shifts the day).

export const ISRAEL_TZ = 'Asia/Jerusalem';

const HAS_OFFSET = /(?:[zZ]|[+-]\d{2}(?::?\d{2})?)$/;
const DATE_TIME_LIKE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/** Parse an API timestamp. No offset/Z -> UTC; with offset/Z -> as is; Date passthrough.
 *  Returns null for empty / unparseable input. */
export function parseUtcTimestamp(v: unknown): Date | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  if (!s) return null;
  let iso = s;
  if (DATE_TIME_LIKE.test(s)) {
    iso = s.replace(' ', 'T');
    if (!HAS_OFFSET.test(iso)) iso += 'Z';
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    iso = `${s}T00:00:00Z`;
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtMinutes = new Intl.DateTimeFormat('en-GB', {
  timeZone: ISRAEL_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const fmtSeconds = new Intl.DateTimeFormat('en-GB', {
  timeZone: ISRAEL_TZ,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function render(f: Intl.DateTimeFormat, d: Date): string {
  const p: Record<string, string> = {};
  for (const part of f.formatToParts(d)) p[part.type] = part.value;
  const time = `${p.hour}:${p.minute}${p.second !== undefined ? `:${p.second}` : ''}`;
  return `${p.day}/${p.month}/${p.year} ${time}`;
}

/** UTC timestamp -> "dd/MM/yyyy HH:mm" (or with :ss) in Israel time. '' when empty/invalid. */
export function formatDateTimeIL(v: unknown, opts?: { seconds?: boolean }): string {
  const d = parseUtcTimestamp(v);
  if (!d) return '';
  return render(opts?.seconds ? fmtSeconds : fmtMinutes, d);
}

/** DATE-only value ("yyyy-MM-dd", optionally followed by a time part) -> "dd/MM/yyyy",
 *  with NO timezone conversion. Unrecognised input is returned unchanged ('' for empty). */
export function formatDateOnly(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(v.getDate())}/${p(v.getMonth() + 1)}/${v.getFullYear()}`;
  }
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}
